import { DragEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import type { TabsProps } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { DropResult } from '@/shared/components/drag-and-drop';
import { setNativeDragPreview } from '@/shared/components/drag-and-drop/nativeDragPreview';

import { NativeNestedResourceTabDragProvider, type NestedResourceTabDropEdge } from './context';
import { getNestedResourceTabDropIndex } from './dropTarget';
import NativeSortableTabNode from './NativeSortableTabNode';

type RenderTabBar = NonNullable<TabsProps['renderTabBar']>;
type TabBarProps = Parameters<RenderTabBar>[0];
type DefaultTabBar = Parameters<RenderTabBar>[1];

export interface NestedResourceSortableTabBarProps {
    allowReorder: boolean;
    droppableId: string;
    onDragEnd: (result: DropResult) => void;
    parentLocked?: boolean;
    resource: 'listViews' | 'reports';
    resourceIds: string[];
}

const NestedResourceSortableTabBar: FC<
    NestedResourceSortableTabBarProps & { DefaultTabBar: DefaultTabBar; tabBarProps: TabBarProps }
> = ({ DefaultTabBar, allowReorder, droppableId, onDragEnd, parentLocked, resource, resourceIds, tabBarProps }) => {
    const { token } = useAntDesignTheme();
    const [activeResourceId, setActiveResourceId] = useState<string>();
    const [activeTabSize, setActiveTabSize] = useState<{ gutter: number; height: number; width: number }>();
    const [dropTarget, setDropTarget] = useState<{ edge: NestedResourceTabDropEdge; resourceId: string }>();
    const cancelDrag = useCallback(() => {
        setActiveResourceId(undefined);
        setActiveTabSize(undefined);
        setDropTarget(undefined);
    }, []);
    const startDrag = useCallback(
        (resourceId: string, event: DragEvent<HTMLElement>) => {
            const source = event.currentTarget.closest<HTMLElement>('[data-platform-sortable-tab-id]');

            if (!allowReorder || parentLocked || !resourceIds.includes(resourceId) || !source) {
                event.preventDefault();
                return;
            }

            const siblings = Array.from(source.parentElement?.children ?? []).filter(
                (element) => element.getAttribute('data-platform-sortable-tab-group') === droppableId,
            );
            const gutter = Math.max(
                0,
                ...siblings.map((element) => {
                    const style = getComputedStyle(element);

                    return Number.parseFloat(tabBarProps.rtl ? style.marginRight : style.marginLeft) || 0;
                }),
            );

            setActiveTabSize({ ...setNativeDragPreview(event, source, token), gutter });
            setActiveResourceId(resourceId);
            setDropTarget({ edge: 'before', resourceId });
        },
        [allowReorder, droppableId, parentLocked, resourceIds, tabBarProps.rtl, token],
    );
    const dropResource = useCallback(
        (targetResourceId: string, edge: NestedResourceTabDropEdge) => {
            if (!activeResourceId || !allowReorder || parentLocked) {
                cancelDrag();
                return;
            }

            const sourceIndex = resourceIds.indexOf(activeResourceId);
            const target =
                targetResourceId === activeResourceId && dropTarget
                    ? dropTarget
                    : { edge, resourceId: targetResourceId };
            const destinationIndex = getNestedResourceTabDropIndex(
                resourceIds,
                activeResourceId,
                target.resourceId,
                target.edge,
            );

            if (destinationIndex === undefined) {
                cancelDrag();
                return;
            }

            const result: DropResult = {
                combine: null,
                destination: { droppableId, index: destinationIndex },
                draggableId: activeResourceId,
                mode: 'FLUID',
                reason: 'DROP',
                source: { droppableId, index: sourceIndex },
                type: 'DEFAULT',
            };

            cancelDrag();
            onDragEnd(result);
        },
        [activeResourceId, allowReorder, cancelDrag, droppableId, dropTarget, onDragEnd, parentLocked, resourceIds],
    );
    useEffect(() => {
        if (activeResourceId && (!allowReorder || parentLocked || !resourceIds.includes(activeResourceId))) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Cancel an in-flight native drag if its resource or edit permission disappears.
            cancelDrag();
        }
    }, [activeResourceId, allowReorder, cancelDrag, parentLocked, resourceIds]);
    const previewResourceIds = useMemo(() => {
        if (!activeResourceId || !dropTarget) {
            return resourceIds;
        }

        const destination = getNestedResourceTabDropIndex(
            resourceIds,
            activeResourceId,
            dropTarget.resourceId,
            dropTarget.edge,
        );

        if (destination === undefined) {
            return resourceIds;
        }

        const preview = resourceIds.filter((id) => id !== activeResourceId);
        preview.splice(destination, 0, activeResourceId);
        return preview;
    }, [activeResourceId, dropTarget, resourceIds]);
    const dragState = useMemo(
        () => ({
            activeResourceId,
            activeTabSize,
            allowReorder,
            cancelDrag,
            droppableId,
            dropResource,
            dropTarget,
            onDragEnd,
            parentLocked,
            previewResourceIds,
            resource,
            resourceIds,
            rtl: tabBarProps.rtl,
            setDropTarget,
            startDrag,
        }),
        [
            activeResourceId,
            activeTabSize,
            allowReorder,
            cancelDrag,
            droppableId,
            dropResource,
            dropTarget,
            onDragEnd,
            parentLocked,
            previewResourceIds,
            resource,
            resourceIds,
            startDrag,
            tabBarProps.rtl,
        ],
    );

    return (
        <NativeNestedResourceTabDragProvider value={dragState}>
            <DefaultTabBar {...tabBarProps}>
                {(node) => {
                    const resourceId = String(node.key ?? '');

                    return resourceIds.includes(resourceId) ? (
                        <NativeSortableTabNode node={node} resourceId={resourceId} />
                    ) : (
                        node
                    );
                }}
            </DefaultTabBar>
        </NativeNestedResourceTabDragProvider>
    );
};

export default NestedResourceSortableTabBar;
